import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { Building2, Eye, EyeOff, Star, MessageSquare, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: getAuthHeaders(), withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "Total Listings", value: stats.total_listings, icon: Building2, color: "text-[#1A1A2E]" },
    { label: "Published", value: stats.published_listings, icon: Eye, color: "text-[#2D8A65]" },
    { label: "Drafts", value: stats.draft_listings, icon: EyeOff, color: "text-[#94A3B8]" },
    { label: "Featured", value: stats.featured_listings, icon: Star, color: "text-[#32A334]" },
    { label: "Inquiries", value: stats.total_inquiries, icon: MessageSquare, color: "text-[#1A1A2E]" },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="px-6 md:px-12 lg:px-24 py-8">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-['DM_Serif_Display'] text-3xl sm:text-4xl text-[#1A1A2E] mb-1">Dashboard</h1>
          <p className="text-[#94A3B8] text-sm">Overview of your properties and inquiries</p>
        </div>
        <Link
          to="/admin/listings/new"
          data-testid="dashboard-add-listing"
          className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-6 py-2.5 uppercase tracking-widest text-xs font-semibold inline-flex items-center gap-2 rounded"
        >
          <Plus className="w-4 h-4" /> Add Listing
        </Link>
      </div>

      {stats ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-grid">
          {cards.map((card, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] p-6 rounded-lg" data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <card.icon className={`w-5 h-5 ${card.color} mb-4`} strokeWidth={1.5} />
              <p className="font-['DM_Serif_Display'] text-3xl font-semibold text-[#1A1A2E]">{card.value}</p>
              <p className="text-[#94A3B8] text-xs uppercase tracking-[0.15em] mt-1">{card.label}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Quick Links */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/admin/listings"
          data-testid="quick-link-listings"
          className="bg-white border border-[#E2E8F0] hover:border-[#32A334]/30 hover:shadow-sm transition-all p-6 flex items-center gap-4 rounded-lg"
        >
          <Building2 className="w-6 h-6 text-[#32A334]" strokeWidth={1.5} />
          <div>
            <p className="text-[#1A1A2E] font-medium">Manage Listings</p>
            <p className="text-[#94A3B8] text-sm">Add, edit, or remove properties</p>
          </div>
        </Link>
        <Link
          to="/admin/inquiries"
          data-testid="quick-link-inquiries"
          className="bg-white border border-[#E2E8F0] hover:border-[#32A334]/30 hover:shadow-sm transition-all p-6 flex items-center gap-4 rounded-lg"
        >
          <MessageSquare className="w-6 h-6 text-[#32A334]" strokeWidth={1.5} />
          <div>
            <p className="text-[#1A1A2E] font-medium">View Inquiries</p>
            <p className="text-[#94A3B8] text-sm">Review guest messages</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
